import React, { useEffect } from 'react';
import { Box, Button, Link, Typography } from '@material-ui/core';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import BugReportIcon from '@mui/icons-material/BugReport';
/* eslint-disable */
import { useUser } from 'app/src/context/userContext';
import { useGetAPI } from 'app/src/Utils/useGetAPI';
/* eslint-enable */

import {
  configApiRef,
  fetchApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';

interface Project {
  project?: string;
}

interface GroupMemberships {
  squad: string;
  department: string;
  business_unit: string;
  default: boolean;
}

interface UserProfile {
  email: string;
  username: string;
  groupMemberships: GroupMemberships[];
}

export interface UserStorage {
  user: UserProfile;
}

export function TechdocsSupportBox() {
  const [data, setData] = React.useState<Project>({} as any);
  const [open, setOpen] = React.useState<boolean>(false);
  const [userData, setUserData] = React.useState<UserStorage>(null as any);
  const [message, setMessage] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<boolean>(false);
  const [submissionMessage, setSubmissionMessage] = React.useState<string>('');

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setMessage('');
    setError(false);
    setLoading(false);
    setSubmissionMessage('');
  };

  const { getUser } = useUser();

  const urlForEndpoint = useGetAPI();
  const identityAPI = useApi(microsoftAuthApiRef);

  const config = useApi(configApiRef);
  const baseURLforBackend = config.getOptionalString('backend.baseUrl');
  const { fetch: backstageFetch } = useApi(fetchApiRef);

  function parseTechdocsFromURL(url = window.location.href) {
    // The regular expression used to parse the URL for the techdocs value that comes immediately after "component/" in the url
    const regex = /\/component\/([^/]+)/;
    const matches = url.match(regex);
    if (matches && matches.length >= 2) {
      return matches[1];
    }

    return null;
  }

  // get user's identity so we can add their email as part of the POST request body to our API endpoint
  useEffect(() => {
    (async () => {
      const user = await getUser();
      setUserData({
        user: user,
      });
    })();
  }, [getUser]);

  // 2 Backstage API calls to get 1. the techdocs metadata and 2. the group information that owns the component
  useEffect(() => {
    async function fetchData() {
      try {
        const urlToGetTechdocsManifest = `${baseURLforBackend}/api/techdocs/metadata/entity/default/component/${parseTechdocsFromURL()}`;
        const infoForTechdocs = await backstageFetch(urlToGetTechdocsManifest);
        const techdocsData = await infoForTechdocs.json();
        const groupThatOwns = techdocsData.spec.owner;
        const infoForGroup = await backstageFetch(
          `${baseURLforBackend}/api/catalog/entities/by-name/group/default/${groupThatOwns}`,
        );
        const groupData = await infoForGroup.json();

        const project = groupData.spec.profile.adoProject
          ? groupData.spec.profile.adoProject
          : 'unknown';

        setData({ project: project });
      } catch (fetchError) {
        setData({ project: 'unknown' });
      }
    }

    fetchData();
  }, [baseURLforBackend, backstageFetch]);

  async function submitIssueToAPI(payload: object) {
    setLoading(true);

    const token = await identityAPI.getIdToken();

    await fetch(`${urlForEndpoint}/v1/api/devops/techdocsTicket`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Basic ${token}`,
      },
      body: JSON.stringify(payload),
    }).then(response => {
      response
        .json()
        .then(dataFromRes => {
          setSubmissionMessage(dataFromRes.message);
          setLoading(false);
        })
        .catch(() => {
          setError(true);
          setSubmissionMessage(
            'An Error occurred with your submission. Please try again in a few minutes.',
          );
          setLoading(false);
        });
    });
  }

  function dialogueContent() {
    if (submissionMessage.length > 0 && error === false) {
      return (
        <DialogContent>
          <div style={{ marginLeft: '40%' }}>
            <CheckIcon
              style={{
                color: '#4BB543',
                width: '40%',
                height: '40%',
                paddingLeft: '16px',
                paddingRight: '16px',
              }}
            />
          </div>

          <Typography style={{ marginBottom: '20px' }}>
            We've submitted a work item for your issue.
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center">
            <Button
              color="secondary"
              href={submissionMessage}
              target="_blank"
              component={Link}
              style={{ alignContent: 'center' }}
            >
              Open link to work item in Azure.
            </Button>
          </Box>
        </DialogContent>
      );
    } else if (submissionMessage.length > 0 && error === true) {
      return (
        <DialogContent>
          <div style={{ marginLeft: '40%' }}>
            <ErrorIcon
              style={{
                color: '#ff3333',
                width: '40%',
                height: '40%',
                paddingLeft: '16px',
                paddingRight: '16px',
              }}
            />
          </div>
          <Typography>
            There was an issue with your request. Please wait a few minutes and
            try again.
          </Typography>
        </DialogContent>
      );
    } else if (error === false && open === true) {
      return (
        <DialogContent>
          <DialogContentText>
            Found an issue with this team's docs? Leave a short description of
            the issue below and a ticket will be opened in the {data.project}{' '}
            project backlog.
          </DialogContentText>

          <TextField
            margin="dense"
            id="issue-techdocs"
            label="Describe the issue below."
            fullWidth
            multiline
            rows={4}
            variant="standard"
            onChange={e => setMessage(e.target.value)}
          />
        </DialogContent>
      );
    }

    return null;
  }

  function isValidSubmission() {
    return (
      message === undefined ||
      message.length <= 1 ||
      loading === true ||
      submissionMessage.length > 0
    );
  }

  return (
    <div className="column" style={{ padding: '8px' }}>
      {data.project === 'unknown' ? null : (
        <>
          {' '}
          <Button onClick={handleClickOpen}>
            <BugReportIcon />
          </Button>
          <Dialog open={open} onClose={handleClose}>
            {dialogueContent()}

            <DialogActions>
              <Button onClick={handleClose}>
                {submissionMessage.length > 0 ? 'Close' : 'Cancel'}
              </Button>

              <Button
                style={{ paddingLeft: '16px', paddingRight: '16px' }}
                disabled={isValidSubmission()}
                onClick={() => {
                  const payload = {
                    user: userData.user.email ? userData.user.email : 'unknown',
                    issueDesc: message,
                    techDocsURL: window.location.href,
                    project: data.project,
                  };
                  submitIssueToAPI(payload);
                }}
              >
                <Typography
                  variant="subtitle2"
                  style={{
                    color: isValidSubmission() ? `#f1e8e8` : `#000000`,
                    paddingLeft: '16px',
                    paddingRight: '16px',
                  }}
                >
                  Submit
                </Typography>
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </div>
  );
}
